import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Card,
  Typography,
  TextField,
  Button,
  Stack,
  useTheme,
  alpha,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
  Grid,
  InputAdornment,
} from '@mui/material';
import { LocationOn, AttachMoney, ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getTutorByUser, updateTutor, clearTutor } from '../../state/slices/tutor';
import { getCurrentUserLocationName } from '../../state/slices/user';
import GoogleMapEmbed from '../../components/map/GoogleMapEmbed';

function TutorLocationForm() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tutorState = useSelector((state) => state.tutor);
  const userState = useSelector((state) => state.user).user;
  const currentLocationState = useSelector((state) => state.user.currentLocation);

  const [formData, setFormData] = useState({
    location: '',
    rate: 100,
  });
  const [errors, setErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  useEffect(() => {
    if (userState && userState.roles.includes('TUTOR') && !tutorState.tutor) {
      dispatch(getTutorByUser(userState.id));
    }
  }, [userState, dispatch, tutorState.tutor]);

  useEffect(() => {
    if (tutorState.tutor) {
      setFormData({
        location: tutorState.tutor.location || '',
        rate: tutorState.tutor.rate || 100,
      });
    }
  }, [tutorState.tutor]);

  useEffect(() => {
    if (currentLocationState.name) {
      setFormData((prev) => ({
        ...prev,
        location: currentLocationState.name,
      }));
    }
  }, [currentLocationState]);

  useEffect(() => {
    // Handle "Tutor not found" gracefully - it's expected for new tutors
    if (tutorState.error && tutorState.error !== 'Tutor not found') {
      setOpenSnackbar(true);
      setSubmitStatus('error');
      setErrors((prev) => ({ ...prev, general: tutorState.error }));
      dispatch(clearTutor());
    } else if (tutorState.error === 'Tutor not found') {
      // Tutor profile doesn't exist yet - this is fine, just clear the error
      dispatch(clearTutor());
    } else if (tutorState.successMsg && tutorState.tutor) {
      setOpenSnackbar(true);
      setSubmitStatus('success');
      setTimeout(() => {
        dispatch(clearTutor());
        navigate('/tutor-profile');
      }, 2000);
    }
  }, [tutorState.error, tutorState.successMsg, tutorState.tutor, navigate, dispatch]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const getCoordinates = () => {
    const options = {
      timeout: 5000,
      maximumAge: 0,
    };

    function success(pos) {
      const crd = pos.coords;
      const lat = crd.latitude.toString();
      const lng = crd.longitude.toString();
      dispatch(getCurrentUserLocationName({ longitude: lng, latitude: lat }));
    }

    function error(err) {
      setErrors((prev) => ({
        ...prev,
        location: t('tutorProfile.locationCannotBeDetermined'),
      }));
      console.warn(`ERROR(${err.code}): ${err.message}`);
    }

    navigator.geolocation.getCurrentPosition(success, error, options);
  };

  const validateForm = () => {
    const validationErrors = {};
    if (!formData.location.trim()) {
      validationErrors.location = t('tutorProfile.locationRequired');
    }
    if (!formData.rate) {
      validationErrors.rate = t('tutorProfile.rateRequired');
    }
    if (formData.rate < 100) {
      validationErrors.rate = t('tutorProfile.rateGreaterThan100');
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = () => {
    // Clear previous errors
    setErrors({});
    dispatch(clearTutor());
    
    if (validateForm()) {
      const data = new FormData();
      data.set('location', JSON.stringify(formData.location));
      data.set('rate', JSON.stringify(formData.rate));
      dispatch(updateTutor(data));
    } else {
      setOpenSnackbar(true);
      setSubmitStatus('error');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/tutor-profile')}
          sx={{ mb: 3, color: theme.palette.primary.main }}
        >
          {t('common.back')}
        </Button>

        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            p: 4,
            background: 'white',
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <LocationOn sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
              {t('tutorProfile.locationAndRate')}
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {errors.general && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {errors.general}
            </Alert>
          )}

          <Stack spacing={4}>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}
              >
                {t('tutorProfile.preferredLocation')}
              </Typography>

              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label={t('tutorProfile.currentResidenceArea')}
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    error={Boolean(errors.location)}
                    helperText={errors.location}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<LocationOn />}
                    onClick={getCoordinates}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '&:hover': {
                        borderColor: theme.palette.primary.dark,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    {t('tutorProfile.useCurrentLocation')}
                  </Button>
                </Grid>
              </Grid>
              
              {/* Google Maps Embed */}
              {formData.location && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                    {t('tutorProfile.locationOnMap')}
                  </Typography>
                  <GoogleMapEmbed location={formData.location} height={300} />
                </Box>
              )}
            </Box>

            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}
              >
                {t('tutorProfile.rate')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                {t('tutorProfile.rateInstructions')}
              </Typography>

              <TextField
                label={t('tutorProfile.ratePerHour')}
                type="number"
                name="rate"
                value={formData.rate}
                onChange={handleChange}
                error={Boolean(errors.rate || formData.rate < 100)}
                helperText={
                  errors.rate ||
                  (formData.rate < 100 ? t('tutorProfile.rateGreaterThan100') : '')
                }
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/tutor-profile')}
                sx={{ borderRadius: 2 }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={tutorState.loading}
                sx={{
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  minWidth: '150px',
                }}
              >
                {tutorState.loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('common.save')
                )}
              </Button>
            </Box>
          </Stack>
        </Card>
      </Container>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={submitStatus === 'success' ? 'success' : 'error'}
          sx={{ borderRadius: 2 }}
        >
          {submitStatus === 'success'
            ? t('tutorProfile.profileUpdatedSuccess')
            : t('tutorProfile.profileUpdateFailed')}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TutorLocationForm;

